(() => {
  if (window.BasedSingleAccordion) {
    window.BasedSingleAccordion.init();
    return;
  }

  const containerSelector = '[data-based-single-accordion]';
  const animationDuration = 180;
  const animationEasing = 'cubic-bezier(0.2, 0, 0.2, 1)';
  const animationTokens = new WeakMap();

  const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const getPanel = (item) =>
    Array.from(item.children).find((child) => child instanceof HTMLElement && child.tagName !== 'SUMMARY');

  const getNextAnimationToken = (item) => {
    const token = (animationTokens.get(item) || 0) + 1;
    animationTokens.set(item, token);
    return token;
  };

  const isCurrentAnimation = (item, token) => animationTokens.get(item) === token;

  const resetPanelStyles = (panel) => {
    panel.style.height = '';
    panel.style.opacity = '';
    panel.style.overflow = '';
  };

  const cancelPanelAnimations = (panel) => {
    panel.getAnimations?.().forEach((animation) => animation.cancel());
  };

  const animateItem = (item, shouldOpen) => {
    const panel = getPanel(item);

    if (!panel || prefersReducedMotion() || typeof panel.animate !== 'function') {
      item.open = shouldOpen;
      return;
    }

    const token = getNextAnimationToken(item);
    cancelPanelAnimations(panel);
    item.dataset.basedAccordionAnimating = 'true';

    if (shouldOpen) {
      item.open = true;
      panel.style.overflow = 'hidden';
      panel.style.height = '0px';
      panel.style.opacity = '0';

      const targetHeight = panel.scrollHeight;
      const animation = panel.animate(
        [
          { height: '0px', opacity: 0 },
          { height: `${targetHeight}px`, opacity: 1 },
        ],
        { duration: animationDuration, easing: animationEasing }
      );

      panel.style.height = `${targetHeight}px`;
      panel.style.opacity = '1';

      animation.finished
        .catch(() => {})
        .then(() => {
          if (!isCurrentAnimation(item, token)) return;

          item.open = true;
          delete item.dataset.basedAccordionAnimating;
          resetPanelStyles(panel);
        });

      return;
    }

    if (!item.open) return;

    const currentHeight = panel.getBoundingClientRect().height || panel.scrollHeight;
    panel.style.overflow = 'hidden';
    panel.style.height = `${currentHeight}px`;
    panel.style.opacity = '1';

    const animation = panel.animate(
      [
        { height: `${currentHeight}px`, opacity: 1 },
        { height: '0px', opacity: 0 },
      ],
      { duration: animationDuration, easing: animationEasing }
    );

    panel.style.height = '0px';
    panel.style.opacity = '0';

    animation.finished
      .catch(() => {})
      .then(() => {
        if (!isCurrentAnimation(item, token)) return;

        item.open = false;
        delete item.dataset.basedAccordionAnimating;
        resetPanelStyles(panel);
      });
  };

  const closeSiblings = (container, openedItem) => {
    Array.from(container.children).forEach((item) => {
      if (item instanceof HTMLDetailsElement && item !== openedItem && item.open) {
        animateItem(item, false);
      }
    });
  };

  const initContainer = (container) => {
    if (!(container instanceof HTMLElement) || container.dataset.basedSingleAccordionReady === 'true') return;

    container.dataset.basedSingleAccordionReady = 'true';
    container.addEventListener('click', (event) => {
      if (prefersReducedMotion() || !(event.target instanceof Element)) return;

      const summary = event.target.closest('summary');
      const item = summary?.parentElement;

      if (!(item instanceof HTMLDetailsElement) || item.closest(containerSelector) !== container) return;

      event.preventDefault();

      if (item.open) {
        animateItem(item, false);
        return;
      }

      closeSiblings(container, item);
      animateItem(item, true);
    });

    container.addEventListener(
      'toggle',
      (event) => {
        const openedItem = event.target;

        if (!(openedItem instanceof HTMLDetailsElement) || !openedItem.open) return;
        if (openedItem.closest(containerSelector) !== container) return;
        if (openedItem.dataset.basedAccordionAnimating === 'true') return;

        Array.from(container.children).forEach((item) => {
          if (item instanceof HTMLDetailsElement && item !== openedItem) {
            item.open = false;
          }
        });
      },
      true
    );
  };

  const init = (root = document) => {
    if (root instanceof HTMLElement && root.matches(containerSelector)) {
      initContainer(root);
    }

    root.querySelectorAll?.(containerSelector).forEach(initContainer);
  };

  window.BasedSingleAccordion = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
